/**
 * @file gui.js
 * @description Main GUI App Controller.
 * @version 6.3.0 (FIXED: Removed redundant bootstrap, delegated to core.js)
 */

import { Utils } from "../../system/utils/utils.js";
import { Sidebar } from "./components/Sidebar.js";
import { Modal } from "./components/Modal.js";
import { HomePage } from "./components/HomePage.js";
import { ProjectsPage } from "./components/ProjectsPage.js";
import { BlogPage } from "./components/BlogPage.js";
import { AboutPage } from "./components/AboutPage.js";
import { ContactPage } from "./components/ContactPage.js";

export class PortfolioUI {
    constructor(kernel) {
        this.kernel = kernel;
        this.stateManager = this.kernel.get('state');
        this.eventBus = this.kernel.get('eventBus');
        this.contentManager = this.kernel.get('content');
        this.dom = {};
        this.components = {};
        this.Utils = Utils;
    }

    async loadHtml() {
        try {
            const response = await fetch('modules/gui/gui.html');
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const html = await response.text();
            
            const guiContainer = document.getElementById('gui-container-wrapper');
            if (guiContainer) {
                guiContainer.innerHTML = html;
            } else {
                console.error('[GUI] Wrapper #gui-container-wrapper not found.');
            }
        } catch (e) {
            console.error('[GUI] Error loading HTML:', e);
        }
    }

    init() {
        // 1. Cache DOM Elements
        this.dom = {
            navItems: document.querySelectorAll('.nav-item'),           
            mobileNavItems: document.querySelectorAll('.mobile-nav-item'), 
            sections: document.querySelectorAll('.page-section'),
            windowTitle: document.getElementById('gui-window-title'),
            contentScrollable: document.querySelector('.terminal-content') 
        };
        
        // 2. Initialize Components
        this.initializeComponents();

        // 3. Register Event Listeners
        this.setupEventListeners();

        // 4. Handle View State Sync
        this.stateManager.subscribe('ui.view', (newView) => {
            const guiContainer = document.getElementById('gui-container');
            if (guiContainer) guiContainer.setAttribute('data-view', newView);
        });

        // ✅ REMOVED: Bootstrap navigation logic
        // This is now handled in core.js AFTER all components are ready
    }

    initializeComponents() {
        this.components.sidebar = new Sidebar({
            eventBus: this.kernel.get('eventBus'),
            state: this.kernel.get('state'),
            theme: this.kernel.get('theme')
        });
        this.components.sidebar.init();
        
        this.components.modal = new Modal({
            eventBus: this.kernel.get('eventBus'),
            content: this.kernel.get('content'),
            state: this.kernel.get('state') 
        });
        this.components.modal.init();

        this.components.home = new HomePage({
            content: this.kernel.get('content'),
            state: this.kernel.get('state'),
            eventBus: this.kernel.get('eventBus')
        });
        this.components.home.init();

        this.components.projects = new ProjectsPage({
            content: this.kernel.get('content'),
            state: this.kernel.get('state'),
            eventBus: this.kernel.get('eventBus')
        });
        this.components.projects.init();
        
        this.components.blog = new BlogPage({
            content: this.kernel.get('content'),
            state: this.kernel.get('state'),
            eventBus: this.kernel.get('eventBus')
        });
        this.components.blog.init();
        
        this.components.about = new AboutPage({
            content: this.kernel.get('content'),
            eventBus: this.kernel.get('eventBus')
        });
        this.components.about.init();
        
        this.components.contact = new ContactPage({
            content: this.kernel.get('content'),
            state: this.kernel.get('state'),
            eventBus: this.kernel.get('eventBus')
        });
        this.components.contact.init();
    }

    setupEventListeners() {
        // Core Navigation Listener
        this.eventBus.on('page:navigate', ({ section, id }) => {
            this.showPage(section, id);
        });

        // Desktop Nav
        this.dom.navItems.forEach(item => {
            const handler = (e) => {
                e.preventDefault();
                const sectionId = item.dataset.section;
                this.stateManager.navigate([sectionId]);
            };
            
            item.addEventListener('click', handler);
            item.addEventListener('touchstart', handler, { passive: false }); // ADD THIS
        });

        // Mobile Nav
        if (this.dom.mobileNavItems) {
            this.dom.mobileNavItems.forEach(item => {
                const handler = (e) => {
                    e.preventDefault();
                    const sectionId = item.dataset.section;
                    this.stateManager.navigate([sectionId]);
                };
                
                item.addEventListener('click', handler);
                item.addEventListener('touchstart', handler, { passive: false }); // ADD THIS
            });
        }

        // Link Handling
        this.eventBus.on('link:cinematic', ({ url, type }) => {
            if (type === 'mail') {
                this.eventBus.emit('modal:open:mail', { mailtoUrl: url });
            } else {
                window.open(url, '_blank');
            }
        });

        // Modal Handling
        this.eventBus.on('modal:open:skill', this.handleSkillModal.bind(this));
    }

    /**
     * Updates the GUI to show the selected section.
     * @param {string} sectionId - The section to show (home, projects, etc.)
     * @param {string|null} detailId - Optional detail ID (project/blog ID)
     */
    showPage(sectionId, detailId = null) {
        // Validate Target Section
        let targetSection = sectionId || 'home';
        const targetElement = document.getElementById(targetSection);
        
        if (!targetElement) {
            console.warn(`[GUI] Section "${targetSection}" not found. Defaulting to home.`);
            targetSection = 'home';
        }

        console.log(`[GUI] showPage called: ${targetSection}${detailId ? '/' + detailId : ''}`);

        // 1. UPDATE DESKTOP SIDEBAR
        this.dom.navItems.forEach(item => item.classList.remove('active'));
        
        this.dom.navItems.forEach(item => {
            const itemSection = (item.dataset.section || '').trim().toLowerCase();
            const target = targetSection.trim().toLowerCase();
            
            if (itemSection === target) {
                item.classList.add('active');
                if (this.dom.windowTitle) {
                    const textSpan = item.querySelector('.nav-text');
                    if (textSpan) this.dom.windowTitle.textContent = textSpan.textContent;
                }
            }
        });

        // 2. UPDATE MOBILE NAV
        if (this.dom.mobileNavItems) {
            this.dom.mobileNavItems.forEach(item => item.classList.remove('active'));
            this.dom.mobileNavItems.forEach(item => {
                const itemSection = (item.dataset.section || '').trim().toLowerCase();
                if (itemSection === targetSection.trim().toLowerCase()) {
                    item.classList.add('active');
                }
            });
        }

        // 3. SWITCH CONTENT SECTION
        this.dom.sections.forEach(section => section.classList.remove('active'));
        const newSection = document.getElementById(targetSection);
        if (newSection) {
            newSection.classList.add('active');
            if (this.dom.contentScrollable) {
                this.dom.contentScrollable.scrollTo(0, 0);
            }
        }

        // 4. HANDLE DEEP LINKING (Open Modal)
        if (detailId) {
            console.log(`[GUI] Deep link: Opening detail for ${targetSection}/${detailId}`);
            setTimeout(() => {
                if (targetSection === 'projects') {
                    this.eventBus.emit('modal:open:project', { projectId: detailId });
                } else if (targetSection === 'blog') {
                    this.eventBus.emit('modal:open:blog', { postId: detailId });
                }
            }, 100); 
        }

        // 5. SET GLOBAL VIEW STATE
        const viewMode = targetSection === 'terminal' ? 'terminal' : 'gui';
        this.stateManager.set('ui.view', viewMode);
        if (viewMode === 'terminal') {
            this.eventBus.emit('terminal:focus');
        }
    }

    handleSkillModal(data) {
        const { skillName } = data;
        const content = this.contentManager.getContentByTag(skillName);

        if (content.projects.length === 0 && content.blogs.length === 0) {
             const notFoundHtml = `
                <h2 class="modal-title">Related to: ${skillName}</h2>
                <p>No related projects or field reports found for this skill.</p>
            `;
            this.eventBus.emit('modal:open:html', { html: notFoundHtml });
            return;
        }

        let modalHtml = `<h2 class="modal-title">Related to: ${skillName}</h2>`;

        if (content.projects.length > 0) {
            modalHtml += `
                <h3 class="subsection-title modal-subsection">Projects</h3>
                <ul class="modal-link-list">
                    ${content.projects.map(p => `
                        <li><a href="#" class="modal-link" data-project-id="${p.id}">${p.title}</a></li>
                    `).join('')}
                </ul>
            `;
        }
        
        if (content.blogs.length > 0) {
            modalHtml += `
                <h3 class="subsection-title modal-subsection">Field Reports</h3>
                <ul class="modal-link-list">
                    ${content.blogs.map(b => `
                        <li><a href="#" class="modal-link" data-post-id="${b.id}">${b.title}</a></li>
                    `).join('')}
                </ul>
            `;
        }

        this.eventBus.emit('modal:open:html', { html: modalHtml });
    }
}